import { useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Flex,
  Input,
  List,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GetProcessRollbackStudents } from '../../helper/apiHelper';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import type { RollbackStudent } from '../../Types/interfaces';

interface RollbackStudentListProps {
  studyYearId: number;
  selectedStudentId?: number;
  onSelect: (student: RollbackStudent) => void;
  // collapsed: only the initials are shown, the full name is in the tooltip
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

// lower case without diacritics, so "muresan" finds "Mureșan"
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getInitials = (student: RollbackStudent) =>
  `${student.lastName?.[0] ?? ''}${student.firstName?.[0] ?? ''}`.toUpperCase();

export const RollbackStudentList = ({
  studyYearId,
  selectedStudentId,
  onSelect,
  collapsed,
  onToggleCollapsed,
}: RollbackStudentListProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: students = [], isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.PROCESS_ROLLBACK_STUDENTS, studyYearId],
    queryFn: () => GetProcessRollbackStudents(studyYearId),
  });

  const filteredStudents = useMemo(() => {
    const query = normalize(search.trim());
    if (!query) return students;
    return students.filter((student) =>
      normalize(
        `${student.firstName} ${student.lastName} ${student.lastName} ${student.firstName} ${student.email}`
      ).includes(query)
    );
  }, [students, search]);

  if (collapsed) {
    return (
      <Card styles={{ body: { padding: '12px 8px' } }}>
        <Flex vertical align="center" gap={8}>
          <Tooltip
            title={t('processRollback.expandStudentList')}
            placement="right"
          >
            <Button
              icon={<MenuUnfoldOutlined />}
              aria-label={t('processRollback.expandStudentList')}
              aria-expanded={false}
              onClick={onToggleCollapsed}
            />
          </Tooltip>
          <Typography.Text type="secondary">{students.length}</Typography.Text>
          {isLoading ? (
            <Spin size="small" />
          ) : (
            // wraps into rows when the column is full width on small screens
            <Flex
              wrap
              justify="center"
              gap={8}
              style={{ maxHeight: 600, overflowY: 'auto' }}
            >
              {/* the search is hidden here, so every student is listed */}
              {students.map((student) => {
                const isSelected = student.id === selectedStudentId;
                const fullName = `${student.firstName} ${student.lastName}`;
                return (
                  <Tooltip key={student.id} title={fullName} placement="right">
                    <Button
                      shape="circle"
                      size="large"
                      type={isSelected ? 'primary' : 'default'}
                      aria-label={fullName}
                      aria-current={isSelected || undefined}
                      onClick={() => onSelect(student)}
                    >
                      {getInitials(student)}
                    </Button>
                  </Tooltip>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Card>
    );
  }

  return (
    <Card
      title={t('processRollback.students')}
      extra={
        <Flex align="center" gap={8}>
          <Typography.Text type="secondary">
            {t('processRollback.studentCount', { count: students.length })}
          </Typography.Text>
          <Tooltip title={t('processRollback.collapseStudentList')}>
            <Button
              type="text"
              icon={<MenuFoldOutlined />}
              aria-label={t('processRollback.collapseStudentList')}
              aria-expanded
              onClick={onToggleCollapsed}
            />
          </Tooltip>
        </Flex>
      }
    >
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder={t('processRollback.searchPlaceholder')}
        aria-label={t('processRollback.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
      />
      <List
        loading={isLoading}
        dataSource={filteredStudents}
        locale={{ emptyText: t('processRollback.noStudents') }}
        style={{ maxHeight: 600, overflowY: 'auto' }}
        renderItem={(student) => {
          const isSelected = student.id === selectedStudentId;
          return (
            <List.Item
              onClick={() => onSelect(student)}
              style={{
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: 8,
                background: isSelected ? '#eef1fb' : undefined,
              }}
            >
              <List.Item.Meta
                avatar={<Avatar>{getInitials(student)}</Avatar>}
                title={`${student.lastName} ${student.firstName}`}
                description={student.email}
              />
              <Typography.Text type="secondary">
                {t('processRollback.processCount', {
                  count: student.processIds.length,
                })}
              </Typography.Text>
            </List.Item>
          );
        }}
      />
    </Card>
  );
};
